'use client';

import { Button } from '@/components/ui/button';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from 'react-share';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useUserInfo } from '@/provider/UserInfoProvider';
import { useSignupModal } from '@/zustand/store';
import { toast } from 'sonner';
import { generateInviteCode } from '../utils/generateInviteCode';

export default function Share() {
  const title = '🚀 Unlock Exclusive Rewards with Kaze.ai! 🎉';
  const [shouldMutate, setShouldMutate] = useState(false);
  const { userIdentity, userInfo, isLoadingUser } = useUserInfo();
  const setSignupModalOpen = useSignupModal((s) => s.setSignupModalOpen);
  const isLoginUser = userIdentity === 3;
  const [shareUrl, setShareUrl] = useState('');
  const [description, setDescription] = useState('');
  const [open, setOpen] = useState(false);
  const { mutate, isPending } = useMutation({
    mutationFn: () => generateInviteCode({ userIdentity, userInfo }),
    onSuccess: (data) => {
      const url = `${window.location.origin}/watermark-removal?invite_code=${data!.invite_code}&utm_source=referal&utm_medium=referal&utm_campaign=referal&utm_content=${data!.invite_code}`;
      const text = `
      Hey! I’m using Kaze.ai to remove watermarks instantly and thought you’d love it too!
      👉 Sign up & try removing a watermark – you’ll get 3 free credits!
      ✨ Fast. Easy. No hassle.
      Don’t miss out! Click below and start editing like a pro:
      🔗 ${url}
      Let’s make image editing seamless and fun! 🙌`;
      setShareUrl(url);
      setDescription(text);
      setOpen(true);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (!isLoadingUser && userIdentity === 3 && shouldMutate && userInfo) {
      mutate();
      setShouldMutate(false);
    }
  }, [isLoadingUser, userIdentity, mutate, shouldMutate, userInfo]);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          onClick={(e) => {
            !isLoginUser && e.preventDefault();
            if (shareUrl) return;
            isLoginUser
              ? mutate()
              : setSignupModalOpen(true, {
                  signupSuccess: () => setShouldMutate(true),
                  description: 'Sign up to get your invite link!',
                });
          }}
          className={`h-11 rounded-[0.375rem] bg-gray-800 text-base font-medium text-stone-50 ${isPending ? 'pointer-events-none' : 'pointer-events-auto'}`}
        >
          Share
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className='text-center leading-normal'>
            🚀 Share your link with your friends and get 25% off! 🎉
          </DrawerTitle>
        </DrawerHeader>
        <DrawerDescription />
        <div className='mt-4 flex flex-row justify-evenly pb-20'>
          <FacebookShareButton url={shareUrl}>
            <FacebookIcon size={48} round />
          </FacebookShareButton>
          <TwitterShareButton url={description} title={title}>
            <XIcon size={48} round />
          </TwitterShareButton>
          <WhatsappShareButton url={description} title={title} separator=':: '>
            <WhatsappIcon size={48} round />
          </WhatsappShareButton>
          <EmailShareButton url={description} subject={title}>
            <EmailIcon size={48} round />
          </EmailShareButton>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
